import {
  Action,
  Attributes,
  AttributesRow,
  Button,
  DataStatus,
  Flag,
  openDeleteDialog,
  openDialog,
  Table,
  TableRow,
} from 'platform/components';
import {Box, Heading, HStack, Show, Text, VStack} from 'platform/foundation';
import {useCurrencySymbolFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {isEmpty} from 'ramda';

import {CustomFieldPropertyResponseBody, useGetTenantQuery} from '@dms/api/core';
import {
  TenantFieldDefinitionResponseBody,
  useDeleteFieldDefinitionMutation,
} from '@dms/api/settings';
import {DraggableCard} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {handleApiError, usePermissions} from '@dms/shared';

import {buildArray, DOT_CHARACTER, Nullish, RequiredTestIdProps} from 'shared/web';

import {BaseDefinitionUpdateForm} from '../(sections)/UpdateDefinition/BaseDefinitionUpdateForm';
import {CustomFieldPropertyUpdateForm} from '../(sections)/UpdateDefinition/CustomFieldPropertyUpdateForm';
import {EnumDefinitionUpdateForm} from '../(sections)/UpdateDefinition/EnumDefinitionUpdateForm';
import {VALUE_TYPE_OPTIONS} from '../constants/valueTypeOptions';
import {getValueTypeLabel} from '../utils/getValueTypeLabel';

interface DefinitionCardProps extends RequiredTestIdProps {
  definition: TenantFieldDefinitionResponseBody;
  customFieldProperty: CustomFieldPropertyResponseBody | Nullish;
  handleProps?: React.HTMLAttributes<HTMLDivElement>;
}

export function DefinitionCard(props: DefinitionCardProps) {
  const formatCurrencySymbol = useCurrencySymbolFormatter();

  const [canUpdateDefinition, canRemoveDefinition] = usePermissions({
    permissionKeys: ['updateCustomFieldsDefinitions', 'removeCustomFieldsDefinitions'],
  });

  const {data: tenant} = useGetTenantQuery();
  const currencySymbol = formatCurrencySymbol(tenant?.currency);

  const [deleteDefinition] = useDeleteFieldDefinitionMutation();

  const handleDeleteDefinition = () =>
    openDeleteDialog({
      text: i18n.t('page.generalSettings.labels.deleteDefinitionDialogText', {
        name: props.definition.name,
      }),
      'data-testid': testIds.settings.customFields('deleteRoleConfirmDialog'),
      onConfirm: async () => {
        await deleteDefinition({
          definitionId: props.definition.id,
          resourceId: props.definition.resource.id,
        })
          .unwrap()
          .catch(handleApiError);
      },
    });

  const handleEditBaseDefinition = () => {
    const valueTypeLabel =
      VALUE_TYPE_OPTIONS.find((option) => option.value === props.definition.valueType)?.label ||
      props.definition.valueType;

    openDialog(
      <BaseDefinitionUpdateForm
        definition={props.definition}
        data-testid={testIds.settings.customFields('updateBaseDefinition')}
      />,
      {
        'data-testid': testIds.settings.customFields('updateBaseDefinition'),
        title: `${props.definition.name} ${DOT_CHARACTER} ${valueTypeLabel}`,
      }
    );
  };

  const handleEditEnumValues = () =>
    openDialog(
      <EnumDefinitionUpdateForm
        definitionId={props.definition.id}
        resourceId={props.definition.resource.id}
        data-testid={testIds.settings.customFields('updateEnumDefinition')}
      />,
      {
        'data-testid': testIds.settings.customFields('updateEnumDefinition'),
        title: i18n.t('page.generalSettings.labels.enumTitle'),
      }
    );

  const handleEditTemplateValues = () =>
    openDialog(
      <CustomFieldPropertyUpdateForm
        customFieldDefinitionId={props.definition.id}
        data-testid={testIds.settings.customFields('updateCustomFieldProperty')}
      />,
      {
        'data-testid': testIds.settings.customFields('updateCustomFieldProperty'),
        title: i18n.t('page.generalSettings.actions.updateTemplateValueTitle'),
      }
    );

  const valueTypeLabel = getValueTypeLabel({...props.definition, currencySymbol});
  const isNotified = props.definition.isNotified;
  const isRequired = props.definition.isRequired;

  const baseRows = buildArray<AttributesRow>()
    .add({
      label: i18n.t('page.generalSettings.labels.valueType'),
      value: valueTypeLabel,
    })
    .add({
      label: i18n.t('page.generalSettings.labels.name'),
      value: props.definition.name,
    })
    .add({
      label: i18n.t('page.generalSettings.labels.isFieldRequired'),
      content: (
        <Flag
          size="small"
          colorScheme={isRequired ? 'green' : 'red'}
          label={isRequired ? i18n.t('general.labels.yes') : i18n.t('general.labels.no')}
        />
      ),
    })
    .add({
      label: i18n.t('page.generalSettings.labels.hint'),
      value: props.definition.hint,
    })
    .when(props.definition.valueType === 'date' || props.definition.valueType === 'datetime', {
      label: i18n.t('page.generalSettings.labels.isNotified'),
      content: (
        <Flag
          size="small"
          colorScheme={isNotified ? 'green' : 'red'}
          label={isNotified ? i18n.t('general.labels.yes') : i18n.t('general.labels.no')}
        />
      ),
    })
    .when(props.definition.valueType === 'boolean', {
      label: i18n.t('page.generalSettings.labels.defaultValue'),
      content: (
        <Flag
          size="small"
          colorScheme="neutral"
          label={match(props.definition.defaultValue)
            .with(true, () => i18n.t('general.labels.yes'))
            .with(false, () => i18n.t('general.labels.no'))
            .otherwise(() => i18n.t('general.labels.empty'))}
        />
      ),
    });

  const templateRows = buildArray<AttributesRow>()
    .add({
      label: i18n.t('page.generalSettings.labels.templateValueName'),
      value: props.customFieldProperty?.propertyName || null,
    })
    .add({
      label: i18n.t('page.generalSettings.labels.templateValue'),
      value: props.customFieldProperty?.documentPropertyName || null,
    });

  return (
    <DraggableCard
      title={props.definition.name}
      subtitle={valueTypeLabel}
      handleProps={props.handleProps}
      data-testid={testIds.settings.customFields(`card-${props.definition.resource.id}`)}
      actions={buildArray<Action>().when(canRemoveDefinition, {
        type: 'iconButton',
        icon: 'action/delete',
        severity: 'danger',
        onClick: handleDeleteDefinition,
      })}
    >
      <VStack spacing={4}>
        <VStack>
          <HStack justify="space-between" align="center">
            <Heading size={4}>{i18n.t('page.generalSettings.labels.baseTitle')}</Heading>
            <Show when={canUpdateDefinition}>
              <Button
                leftIcon="image/edit"
                size="small"
                variant="ghostLink"
                title={i18n.t('general.actions.edit')}
                data-testid={testIds.settings.customFields('updateBaseDefinition')}
                onClick={handleEditBaseDefinition}
              />
            </Show>
          </HStack>
          <Attributes size="third" rows={baseRows} />
        </VStack>

        <VStack>
          <HStack justify="space-between" align="center">
            <Heading size={4}>{i18n.t('page.generalSettings.labels.templateTitle')}</Heading>
            <Show when={canUpdateDefinition}>
              <Button
                leftIcon="image/edit"
                size="small"
                variant="ghostLink"
                title={i18n.t('general.actions.edit')}
                data-testid={testIds.settings.customFields('updateCustomFieldProperty')}
                onClick={handleEditTemplateValues}
              />
            </Show>
          </HStack>
          <Attributes size="third" rows={templateRows} />
        </VStack>

        <Show
          when={
            props.definition.valueType === 'enum' || props.definition.valueType === 'multi_enum'
          }
        >
          <VStack spacing={2}>
            <HStack justify="space-between" align="center">
              <Heading size={4}>{i18n.t('page.generalSettings.labels.enumTitle')}</Heading>
              <Show when={canUpdateDefinition}>
                <Button
                  leftIcon="image/edit"
                  size="small"
                  variant="ghostLink"
                  title={i18n.t('general.actions.edit')}
                  data-testid={testIds.settings.customFields('updateEnumDefinition')}
                  onClick={handleEditEnumValues}
                />
              </Show>
            </HStack>

            <DataStatus isEmpty={isEmpty(props.definition.enumOptions)}>
              <Table
                variant="bordered"
                tableLayout="fixed"
                data-testid={testIds.settings.customFields('updateEnumDefinition')}
                columns={[
                  {
                    width: '10%',
                  },
                  {
                    width: '60%',
                  },
                  {
                    width: '30%',
                  },
                ]}
              >
                {props.definition.enumOptions.map((option, index) => (
                  <TableRow
                    key={option.uuid}
                    color={option.active ? undefined : 'palettes.neutral.40.40'}
                    data-testid={testIds.settings.customFields('updateEnumDefinition')}
                  >
                    <Box paddingVertical={1}>
                      <Text
                        align="center"
                        size="small"
                        color={option.active ? undefined : 'tertiary'}
                        data-testid={testIds.settings.customFields('updateEnumDefinition')}
                      >
                        {`${index + 1}.`}
                      </Text>
                    </Box>

                    <Box padding={1}>
                      <Text
                        size="small"
                        color={option.active ? undefined : 'tertiary'}
                        data-testid={testIds.settings.customFields('updateEnumDefinition')}
                      >
                        {option.name}
                      </Text>
                    </Box>

                    <Box padding={1}>
                      <Flag
                        size="small"
                        colorScheme={option.active ? 'green' : 'black'}
                        isSubtle={!option.active}
                        data-testid={testIds.settings.customFields('updateEnumDefinition')}
                        label={
                          option.active
                            ? i18n.t('general.labels.active')
                            : i18n.t('entity.employee.labels.flagInactive')
                        }
                      />
                    </Box>
                  </TableRow>
                ))}
              </Table>
            </DataStatus>
          </VStack>
        </Show>
      </VStack>
    </DraggableCard>
  );
}
